import type { ShareOptions } from "@capacitor/share";
import { registerPlugin } from "@capacitor/core";

import {
  nativeAppStoreUrl,
  type ShareBoundaryResult,
  type SharePayload,
} from "../config/share-config";
import {
  NativeResultImage,
  type NativeResultImagePort,
} from "./native-result-image";

export interface NativeSharePort {
  canShare(): Promise<{ readonly value: boolean }>;
  share(options: ShareOptions): Promise<{ readonly activityType?: string }>;
}

export interface WebSharePort {
  canShare?(data?: ShareData): boolean;
  share?(data?: ShareData): Promise<void>;
}

const nativeShare = registerPlugin<NativeSharePort>("Share");

const isAbortError = (error: unknown): boolean =>
  typeof error === "object" && error !== null && "name" in error
    ? error.name === "AbortError"
    : false;

const isNativeCancellation = (error: unknown): boolean =>
  typeof error === "object" && error !== null && "message" in error
    ? error.message === "Share canceled"
    : false;

const approvedAppStoreUrl = (value: string): boolean =>
  /^https:\/\/apps\.apple\.com\/[a-z]{2}\/app\/[a-z0-9-]+\/id[0-9]+$/u.test(
    value,
  );

export const createNativeShareOptions = (
  payload: SharePayload,
  imageUrl: string,
  appStoreUrl: string | null,
): ShareOptions => {
  if (!imageUrl.startsWith("file://")) {
    throw new Error("Native result image URL is invalid");
  }
  if (appStoreUrl !== null && !approvedAppStoreUrl(appStoreUrl)) {
    throw new Error("Native App Store URL is not approved");
  }
  return {
    files: [imageUrl],
    text: payload.nativeText,
    title: payload.title,
    ...(appStoreUrl === null ? {} : { url: appStoreUrl }),
  };
};

export class ShareBoundary {
  public constructor(
    private readonly native: boolean,
    private readonly nativePort: NativeSharePort = nativeShare,
    private readonly webPort: WebSharePort = navigator,
    private readonly nativeImage: NativeResultImagePort = new NativeResultImage(),
    private readonly appStoreUrl: string | null = nativeAppStoreUrl,
  ) {}

  public async share(payload: SharePayload): Promise<ShareBoundaryResult> {
    if (this.native) {
      return this.shareNative(payload);
    }
    return this.shareWeb(payload);
  }

  private async shareNative(
    payload: SharePayload,
  ): Promise<ShareBoundaryResult> {
    try {
      if (!(await this.nativePort.canShare()).value) {
        return "unavailable";
      }
      const imageUrl = await this.nativeImage.create(payload.image);
      await this.nativePort.share(
        createNativeShareOptions(payload, imageUrl, this.appStoreUrl),
      );
      return "shared";
    } catch (error) {
      return isNativeCancellation(error) ? "cancelled" : "failed";
    }
  }

  private async shareWeb(payload: SharePayload): Promise<ShareBoundaryResult> {
    if (this.webPort.share === undefined) {
      return "unavailable";
    }
    const webPayload: ShareData = {
      text: payload.text,
      title: payload.title,
      url: payload.url,
    };
    try {
      if (this.webPort.canShare?.(webPayload) === false) {
        return "unavailable";
      }
      await this.webPort.share(webPayload);
      return "shared";
    } catch (error) {
      return isAbortError(error) ? "cancelled" : "failed";
    }
  }
}
